// src/navigators/LivingHarmonyNavigator.js
import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  Outlet,
} from "react-router-dom";
import LivingHarmonyHeader from "../components/LivingHarmonyHeader";
import ProjectDesignBar from "./ProjectDesignBar";
import IconNav from "./IconNav";
import HideNav from "./HideNav";
import { useAuthWait } from "../auth";
import {
  listProjectsOwnedByUser,
  listRoomsInProject,
} from "../api/ProjectOntologyManager.js";

const links = [
  { label: "Design with Harmony", file: "/" },
  { label: "New Project", file: "/new-project" },
  { label: "Design Studio", file: "/design" },
  { label: "Profile", file: "/profile" },
  { label: "Ontology", file: "/ontology" },
];

export default function LivingHarmonyNavigator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Keep awsCreds for follow-on API calls
  const awsCredsRef = useRef(null);

  // Optionally preselect a project passed via location.state
  const { project: _projectFromLocation } = location.state || {};

  // Sidebar resizing
  const defaultWidth = 360;
  const [sidebarWidth, setSidebarWidth] = useState(defaultWidth);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // App state
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    location: "",
    homeType: "",
    roomsToDesign: [],
    livingWith: [],
    notes: "",
  });

  // These are what the UI consumes for the select + details
  const [roomOptions, setRoomOptions] = useState([
    {
      id: "Bedroom",
      title: "Bedroom",
      description: "Create a restful, rejuvenating sleep space.",
      image: null,
    },
    {
      id: "Home Office",
      title: "Home Office",
      description: "Design a workspace that boosts creativity and focus.",
      image: null,
    },
    {
      id: "Living Room",
      title: "Living Room",
      description: "Turn your living room into a cozy, welcoming retreat.",
      image: null,
    },
    {
      id: "Kitchen",
      title: "Kitchen",
      description: "Optimize your kitchen for both beauty and function.",
      image: null,
    },
    {
      id: "Bathroom",
      title: "Bathroom",
      description: "Refresh your bathroom with calming design touches.",
      image: null,
    },
    {
      id: "Entryway",
      title: "Entryway",
      description: "Make a great first impression at the front door.",
      image: null,
    },
  ]);

  const [selectedRoom, setSelectedRoom] = useState(null);

  // Per-user+project storage key
  const persistKey = useMemo(() => {
    const uid = awsCredsRef.current?.identityId || "anon";
    return `harmony.selectedRoom.${uid}.${selectedProjectId || "no-project"}`;
  }, [selectedProjectId]);

  // Load profile + projects
  const { user, loading } = useAuthWait(async (user, awsCreds) => {
    const userId = awsCreds.identityId;
    awsCredsRef.current = awsCreds;

    try {
      const list = await listProjectsOwnedByUser({ userId, awsCreds });
      setProjects(Array.isArray(list) ? list : []);

      // Priority 1: Use project from location (if provided)
      if (_projectFromLocation?.id) {
        setSelectedProjectId(_projectFromLocation.id);
        return;
      }

      // Priority 2: Auto-select the sole project
      if (Array.isArray(list) && list.length === 1 && list[0]?.id) {
        setSelectedProjectId(list[0].id);
      }
    } catch (err) {
      console.error("[LivingHarmonyNavigator] Failed to load projects:", err);
    }
  });

  // Fetch rooms when a project is selected
  useEffect(() => {
    async function fetchRooms() {
      if (!selectedProjectId || !awsCredsRef.current) return;

      try {
        // Clear previous rooms while loading (do NOT clear selectedRoom)
        setRoomOptions([]);

        const userId = awsCredsRef.current.identityId;
        const rooms = await listRoomsInProject({
          userId,
          projectUuid: selectedProjectId,
          awsCreds: awsCredsRef.current,
        });

        setRoomOptions(rooms);

        // Choose room: keep current if valid -> URL -> localStorage -> first
        const urlRoom = searchParams.get("room");
        const isValid = (id) => !!id && rooms.some((r) => r.id === id);

        let next =
          (isValid(selectedRoom) && selectedRoom) || // keep current if valid
          (isValid(urlRoom) && urlRoom) || // else URL
          (isValid(localStorage.getItem(persistKey)) &&
            localStorage.getItem(persistKey)) || // else storage
          rooms[0]?.id || // else first
          null;

        if (next !== selectedRoom) setSelectedRoom(next);
      } catch (err) {
        console.error("[LivingHarmonyNavigator] Failed to load rooms:", err);
      }
    }

    fetchRooms();
  }, [selectedProjectId]);

  // Keep URL (?room=) and localStorage in sync with selectedRoom
  useEffect(() => {
    if (!selectedRoom) return;
    const current = searchParams.get("room");
    if (current !== selectedRoom) {
      const next = new URLSearchParams(searchParams);
      next.set("room", selectedRoom);
      setSearchParams(next, { replace: true });
    }
    localStorage.setItem(persistKey, selectedRoom);
  }, [selectedRoom, searchParams, setSearchParams, persistKey]);

  // Derived selected project object
  const selectedProject = useMemo(
    () => projects.find((p) => p?.id === selectedProjectId) || null,
    [projects, selectedProjectId],
  );

  // Sidebar handlers
  const handleRestore = () => setSidebarWidth(defaultWidth);
  const handleHide = () => setSidebarWidth(0);
  const onMouseDown = (e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = sidebarWidth;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    if (!isResizing.current) return;
    const delta = e.clientX - startX.current;
    setSidebarWidth(Math.max(0, startWidth.current + delta));
  };
  const onMouseUp = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  // Room selection (accepts native event or direct id)
  const handleRoomSelectionChange = (input) => {
    let id = null;
    if (typeof input === "string") {
      id = input;
    } else if (input?.target) {
      id = input.target.multiple
        ? Array.from(input.target.selectedOptions).map((o) => o.value)[0] ||
          null
        : input.target.value;
    }
    if (!id) return;

    setForm((prev) => ({ ...prev, roomsToDesign: [id] }));
    setSelectedRoom(id);
  };

  // Top nav (preserve query string so ?room= survives)
  const listRef = useRef(null);
  const scrollBy = (offset) =>
    listRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  const handleSelect = (file) =>
    navigate({ pathname: file, search: location.search });

  if (loading) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
      }}
    >
      {/* Header */}
      <header style={{ borderBottom: "1px solid #ddd" }}>
        <LivingHarmonyHeader />
      </header>

      {/* Top menu */}
      <nav className="living-harmony-menu">
        <button
          className="slider-btn left"
          onClick={() => scrollBy(-200)}
          aria-label="Scroll left"
        >
          ◀
        </button>
        <ul className="slider-list" ref={listRef}>
          {links.map(({ label, file }) => (
            <li
              key={file}
              className={`slider-item ${location.pathname === file ? "selected" : ""}`}
              onClick={() => handleSelect(file)}
            >
              {label}
            </li>
          ))}
        </ul>
        <button
          className="slider-btn right"
          onClick={() => scrollBy(200)}
          aria-label="Scroll right"
        >
          ▶
        </button>
      </nav>

      {/* Main content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Restore button when collapsed */}
        {sidebarWidth === 0 && (
          <div style={{ position: "absolute", top: 8, left: 8, zIndex: 1000 }}>
            <IconNav onClick={handleRestore} size={32} />
          </div>
        )}

        {/* Sidebar */}
        <aside
          style={{
            width: sidebarWidth,
            display: "flex",
            flexDirection: "column",
            borderRight: sidebarWidth > 0 ? "1px solid #ddd" : "none",
            transition: "width 0.2s ease",
            overflow: "hidden",
          }}
        >
          {sidebarWidth > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px",
                }}
              >
                <IconNav onClick={handleRestore} size={32} />
                <HideNav onClick={handleHide} />
              </div>

              <div style={{ flex: 1, overflowY: "auto" }}>
                <ProjectDesignBar
                  userId={awsCredsRef.current.identityId}
                  awsCreds={awsCredsRef}
                  projects={projects}
                  // project selection controls
                  selectedProjectId={selectedProjectId}
                  onProjectSelect={setSelectedProjectId}
                  // current project (optional, if the bar uses it)
                  project={selectedProject}
                  // room UI
                  roomOptions={roomOptions}
                  setRoomOptions={setRoomOptions}
                  selectedRoom={selectedRoom}
                  handleRoomSelectionChange={handleRoomSelectionChange}
                />
              </div>

              <div
                onMouseDown={onMouseDown}
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 6,
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 10,
                }}
              />
            </>
          )}
        </aside>

        {/* Content Outlet */}
        <main style={{ flex: 1, overflowY: "auto" }}>
          <Outlet
            context={{
              user,
              project: selectedProject,
              selectedProjectId,
              form,
              roomOptions,
              selectedRoom,
              handleRoomSelectionChange,
            }}
          />
        </main>
      </div>
    </div>
  );
}
